import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2, CheckCircle, Eye, EyeOff, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/errorMessage';
import { redirectToCheckout, track } from '@/lib/analytics';
import { useWorkshopLeaderOffer } from '@/hooks/useWorkshopLeaderOffer';
import { resolveWorkshopLeaderOffer, WORKSHOP_LEADER_PROMO_CODE } from '@/utils/workshopLeaderOffer';
import { cn } from '@/lib/utils';

const PROMO_FALLBACK_NOTICE = 'Enter LEADER50 at checkout to get $100/year.';
const PROMO_FALLBACK_NOTICE_MS = 1600;

export default function WorkshopSignup() {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOffer, resolved } = useWorkshopLeaderOffer(location.search);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [promoFallback, setPromoFallback] = useState(false);
  const [isLeader, setIsLeader] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });

  useEffect(() => {
    if (user) {
      supabase
        .from('workshop_leaders')
        .select('is_active, paid_through')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.is_active && data?.paid_through && new Date(data.paid_through) > new Date()) {
            setIsLeader(true);
          }
        });
    }
  }, [user]);

  const handleGetStarted = async () => {
    if (!user) {
      setShowRegister(true);
      // Scroll to the registration section
      setTimeout(() => {
        document.getElementById('workshop-register')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }

    await startCheckout();
  };

  const offerPromo = () =>
    resolveWorkshopLeaderOffer(location.search, window.sessionStorage)
      ? { promoCode: WORKSHOP_LEADER_PROMO_CODE }
      : {};

  const redirectWhenReady = async (
    data: { url?: string; promoApplied?: boolean } | null,
    sentPromo: boolean,
  ) => {
    if (!data?.url) return false;
    // Missing promoApplied means the deployed function is older than this page.
    if (sentPromo && data.promoApplied !== true) {
      setPromoFallback(true);
      await new Promise((resolve) => window.setTimeout(resolve, PROMO_FALLBACK_NOTICE_MS));
    }
    redirectToCheckout(data.url, 'Workshop Leader');
    return true;
  };

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const body = offerPromo();
      const { data, error } = await supabase.functions.invoke('create-workshop-checkout', {
        body,
      });

      if (await redirectWhenReady(data, 'promoCode' in body)) return;

      if (data?.error?.includes('already have an active') || error?.message?.includes('already have an active')) {
        toast.success('You already have an active subscription!');
        navigate('/workshops/manage');
        return;
      }

      if (error) {
        const errorMsg = error.message || 'Failed to start checkout';
        if (errorMsg.includes('already have an active')) {
          toast.success('You already have an active subscription!');
          navigate('/workshops/manage');
          return;
        }
        throw new Error(errorMsg);
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    setRegisterLoading(true);
    try {
      const normalizedEmail = formData.email.trim().toLowerCase();

      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password: formData.password,
        options: { emailRedirectTo: `${window.location.origin}/workshops/manage` },
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast.error('An account with this email already exists. Please sign in instead.');
          return;
        }
        throw error;
      }

      if (!data.user) throw new Error('Failed to create account');

      try {
        track('sign_up', { method: 'email' });
      } catch {
        // A tracking failure must not skip checkout.
      }

      const session = data.session;
      const credentials = {
        email: normalizedEmail,
        userId: data.user.id,
        accessToken: session?.access_token || null,
        refreshToken: session?.refresh_token || null,
      };

      // Store session tokens for post-payment sign-in
      sessionStorage.setItem('tempCredentials', JSON.stringify(credentials));

      // Note: no account-created email here — workshop leaders get their
      // welcome email from welcome-workshop-leader after Stripe payment completes

      // Create Stripe checkout and redirect
      const checkoutBody = { userCredentials: credentials, ...offerPromo() };
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-workshop-checkout', {
        body: checkoutBody,
      });

      if (checkoutError) {
        // Extract real error from FunctionsHttpError body
        let realMessage = checkoutError.message;
        try {
          const errorContext = checkoutError as unknown as { context?: { body?: BodyInit } };
          if (errorContext?.context?.body) {
            const bodyText = await new Response(errorContext.context.body).text();
            const parsed = JSON.parse(bodyText);
            if (parsed?.error) realMessage = parsed.error;
          }
        } catch { /* ignore */ }
        console.error('[WORKSHOP-SIGNUP] Checkout error:', realMessage);
        throw new Error(realMessage || 'Failed to create checkout session');
      }

      if (!checkoutData?.url) {
        throw new Error('Failed to create checkout session');
      }

      await redirectWhenReady(checkoutData, 'promoCode' in checkoutBody);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Registration failed. Please try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  const features = [
    'Unlimited workshop listings — no per-listing fees',
    'Up to 5 photos per workshop to showcase your work',
    'Location matching — appear when photographers save nearby locations',
    'Dedicated detail page for each workshop with your bio and booking link',
    'Reach photographers actively planning astrophotography trips',
    'Full premium MWP access included — Moon Planner, Trip Planner, and more',
  ];

  return (
    <>
      <SEOHead
        title="List Your Workshop | Milky Way Planner"
        description="List your astrophotography workshops on Milky Way Planner. Reach thousands of photographers planning their next shoot. $200/year, unlimited listings."
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-[#0f1923] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
            <p className="text-gold uppercase text-sm font-semibold tracking-[0.14em] mb-6">
              For Workshop Leaders
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
              List your workshops where<br className="hidden sm:block" />
              photographers are planning
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              Milky Way Planner helps photographers find the best nights and locations for astrophotography.
              Your workshops appear right when they're deciding where to go.
            </p>

            {registerLoading ? (
              <div className="flex items-center gap-3 text-white/70 text-lg">
                <Loader2 className="w-5 h-5 animate-spin" />
                Setting up your account...
              </div>
            ) : isLeader ? (
              <Button
                size="lg"
                onClick={() => navigate('/workshops/manage')}
                className="gap-2 text-base px-8 py-5"
              >
                Manage Your Workshops <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  variant="premium"
                  onClick={handleGetStarted}
                  disabled={checkoutLoading}
                  className={cn('gap-2 text-base px-8 py-5', !resolved && 'invisible')}
                >
                  {checkoutLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</>
                  ) : isOffer ? (
                    <>Get Started — $100/year <ArrowRight className="w-4 h-4" /></>
                  ) : (
                    <>Get Started — $200/year <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
                {promoFallback && (
                  <p className="text-sm text-white mt-4" role="status">{PROMO_FALLBACK_NOTICE}</p>
                )}
                <p className="text-white/40 text-sm mt-4">
                  One student from a $500 workshop covers your listing fee for the year.
                </p>
              </>
            )}
          </div>
        </section>

        {/* Features */}
        <section className="py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-gold-foreground uppercase text-sm font-semibold tracking-[0.14em] mb-4 text-center">
              What You Get
            </p>
            <h2 className="font-serif text-3xl font-bold text-foreground text-center mb-12 tracking-tight">
              Everything you need to fill your workshops
            </h2>
            <div className="space-y-4">
              {features.map((feature) => (
                <div key={feature} className="flex items-start gap-3">
                  <span className="text-[#c9922a] mt-0.5 flex-shrink-0">✦</span>
                  <p className="text-muted-foreground leading-relaxed">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-[#f8fafc] border-y border-border py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-gold-foreground uppercase text-sm font-semibold tracking-[0.14em] mb-4 text-center">
              How It Works
            </p>
            <h2 className="font-serif text-3xl font-bold text-foreground text-center mb-12 tracking-tight">
              Three steps to reach more photographers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                { step: '1', title: 'Sign up & pay', desc: isOffer
                  ? 'Create your account and subscribe for $100/year. Annual billing, cancel anytime.'
                  : 'Create your account and subscribe for $200/year. Annual billing, cancel anytime.' },
                { step: '2', title: 'Add your workshops', desc: 'Set up your profile, then create listings with photos, dates, location, and description.' },
                { step: '3', title: 'Photographers find you', desc: 'Your workshops appear in search results, location matches, and browse pages across MWP.' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-10 h-10 rounded-[4px] bg-[#0f1923] text-white font-serif font-bold text-lg flex items-center justify-center mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className={cn(
                    'text-sm text-muted-foreground leading-relaxed',
                    item.step === '1' && !resolved && 'invisible',
                  )}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20">
          <div className="max-w-md mx-auto px-4 sm:px-6 text-center">
            <p className="text-gold-foreground uppercase text-sm font-semibold tracking-[0.14em] mb-4">
              Simple Pricing
            </p>
            <h2 className="font-serif text-3xl font-bold text-foreground mb-3 tracking-tight">
              One plan. Unlimited workshops.
            </h2>
            <p className="text-muted-foreground mb-8">No per-listing fees. No commission on bookings.</p>

            <div className="rounded-[4px] border border-border p-8">
              <div className="h-1 bg-gradient-to-r from-[#c9922a] to-[#e8b84b] -mt-8 -mx-8 mb-8 rounded-t-[4px]" />
              <div className={cn(
                'font-serif text-5xl font-bold text-foreground mb-1 min-h-[3.75rem]',
                !resolved && 'invisible',
              )}>
                {isOffer ? (
                  <>
                    <del className="line-through text-muted-foreground text-2xl font-normal mr-3 align-middle">
                      <span className="sr-only">was </span>$200
                    </del>
                    <span className="sr-only">now </span>
                    <span>$100</span>
                    <span className="text-xl font-normal text-muted-foreground">/year</span>
                  </>
                ) : (
                  <>
                    $200<span className="text-xl font-normal text-muted-foreground">/year</span>
                  </>
                )}
              </div>
              <p className={cn('text-sm text-muted-foreground mb-6', !resolved && 'invisible')}>
                {isOffer ? 'Billed annually · LEADER50, 50% off for life' : 'Billed annually'}
              </p>

              <ul className="text-sm text-left space-y-3 mb-8">
                {[
                  'Unlimited workshop listings',
                  'Up to 5 photos per workshop',
                  'Your own profile and bio',
                  'Dedicated workshop detail pages',
                  'Location matching for saved locations',
                  'Premium MWP access included',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              {registerLoading ? (
                <Button size="lg" disabled className="w-full gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Setting up...
                </Button>
              ) : isLeader ? (
                <Button size="lg" onClick={() => navigate('/workshops/manage')} className="w-full gap-2">
                  Manage Your Workshops <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    variant="premium"
                    onClick={handleGetStarted}
                    disabled={checkoutLoading}
                    className={cn('w-full gap-2', !resolved && 'invisible')}
                  >
                    {checkoutLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</>
                    ) : isOffer ? (
                      <>Get Started — $100/year <ArrowRight className="w-4 h-4" /></>
                    ) : (
                      <>Get Started <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                  {promoFallback && (
                    <p className="text-sm text-foreground mt-3" role="status">{PROMO_FALLBACK_NOTICE}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* Registration Section — shown when non-logged-in user clicks Get Started */}
        {!user && showRegister && (
          <section id="workshop-register" className="py-16 sm:py-20 bg-[#0f1923]">
            <div className="max-w-lg mx-auto px-4 sm:px-6">
              <div className="text-center mb-8">
                <p className="text-gold uppercase text-sm font-semibold tracking-[0.14em] mb-4">
                  Create Your Account
                </p>
                <h2 className="font-serif text-3xl font-bold text-white mb-3 tracking-tight">
                  Sign up to list your workshops
                </h2>
                <p className="text-white/60 leading-relaxed">
                  {isOffer
                    ? "Create an account and I'll take you to secure checkout at $100/year with LEADER50."
                    : "Create an account and you'll be taken to secure checkout for the $200/year Workshop Leader subscription."}
                </p>
              </div>

              {/* Sign in callout for existing members */}
              <div className="rounded-[4px] bg-[#c9922a]/10 border border-[#c9922a]/30 p-4 mb-8">
                <div className="flex items-start gap-3">
                  <LogIn className="w-5 h-5 text-[#c9922a] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-white font-medium mb-1">Already have an account?</p>
                    <p className="text-sm text-white/60 mb-2">
                      If you're already a Premium member, sign in first so your subscription upgrades automatically — you won't pay for both.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#c9922a] hover:text-[#d4a43a] hover:bg-[#c9922a]/10 px-0"
                      onClick={openAuthModal}
                    >
                      Sign in to your existing account →
                    </Button>
                  </div>
                </div>
              </div>

              {/* Registration form */}
              <div className="rounded-[4px] border border-white/10 bg-white/5 p-8">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="ws-email" className="text-white">Email address</Label>
                    <Input
                      id="ws-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="you@example.com"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ws-password" className="text-white">Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="ws-password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                        placeholder="At least 6 characters"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 text-white/50"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ws-confirm" className="text-white">Confirm password</Label>
                    <Input
                      id="ws-confirm"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                      placeholder="Confirm your password"
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="premium"
                    size="lg"
                    disabled={registerLoading}
                    className={cn('w-full gap-2 mt-2', !resolved && 'invisible')}
                  >
                    {registerLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Creating account...</>
                    ) : isOffer ? (
                      <>Create Account & Pay — $100/year <ArrowRight className="w-4 h-4" /></>
                    ) : (
                      <>Create Account & Pay — $200/year <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                  {promoFallback && (
                    <p className="text-sm text-white text-center" role="status">{PROMO_FALLBACK_NOTICE}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="bg-[#f8fafc] border-t border-border py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-gold-foreground uppercase text-sm font-semibold tracking-[0.14em] mb-4 text-center">
              Questions
            </p>
            <h2 className="font-serif text-3xl font-bold text-foreground text-center mb-10 tracking-tight">
              Frequently asked
            </h2>
            <div className="space-y-8">
              {[
                { q: 'Do I need a Milky Way Planner account?', a: 'Yes, you need a Milky Way Planner account, which you will be given once you sign up for the Workshop Leader subscription. You will have full access to all MWP features.' },
                { q: 'Can I list non-astrophotography workshops?', a: 'Yes — we support astrophotography, deep sky, landscape, nature, and mixed workshop types.' },
                { q: 'Do you take a commission on bookings?', a: 'No. You link to your own booking page. We don\'t handle payments between you and your students.' },
                { q: 'Can I cancel anytime?', a: 'Yes. You can cancel your subscription through your Stripe billing portal. Your listings stay active until the end of your billing period.' },
              ].map((faq) => (
                <div key={faq.q}>
                  <h3 className="font-semibold text-foreground mb-1.5">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
